import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function AnnualFeesMaster() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [fees, setFees] = useState([]);
  const [loadingFees, setLoadingFees] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [formData, setFormData] = useState({
    fee_code: '',
    fee_category: '',
    billing_frequency: '',
    is_mandatory: false,
    discount_applicable: false,
    late_fee_amount: '',
    status: 'draft' // 'draft' or 'published'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const billingFrequencies = [
    'Annual',
    'Termly',
    'Monthly',
    'One-Time'
  ];

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchFees();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchFees = async () => {
    try {
      setLoadingFees(true);
      let query = supabase
        .from('annual_fees_master')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: false });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching fees:', error);
        return;
      }

      setFees(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingFees(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fee_code.trim()) {
      newErrors.fee_code = 'Fee Code is required';
    }

    if (!formData.fee_category.trim()) {
      newErrors.fee_category = 'Fee Category is required';
    }

    if (!formData.billing_frequency) {
      newErrors.billing_frequency = 'Billing Frequency is required';
    }

    if (formData.late_fee_amount && isNaN(parseFloat(formData.late_fee_amount))) {
      newErrors.late_fee_amount = 'Late Fee Amount must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFeeSaveError = (error, fallbackMessage) => {
    if (error?.code === '23505' || error?.message?.includes('unique_fee_code_per_school_branch')) {
      setErrors(prev => ({
        ...prev,
        fee_code: 'This code already exists'
      }));
      alert('This code already exists');
      return;
    }

    alert(fallbackMessage);
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const feeData = {
        ...formData,
        fee_category: formData.fee_category.trim(),
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        status: 'draft',
        late_fee_amount: formData.late_fee_amount ? parseFloat(formData.late_fee_amount) : null,
        updated_at: new Date().toISOString()
      };

      if (editingFee) {
        // Update existing fee
        const { error } = await supabase
          .from('annual_fees_master')
          .update(feeData)
          .eq('id', editingFee.id);

        if (error) throw error;
      } else {
        // Create new fee
        const { error } = await supabase
          .from('annual_fees_master')
          .insert(feeData);

        if (error) throw error;
      }

      // Reset form
      setFormData({
        fee_code: '',
        fee_category: '',
        billing_frequency: '',
        is_mandatory: false,
        discount_applicable: false,
        late_fee_amount: '',
        status: 'draft'
      });
      setEditingFee(null);
      setErrors({});
      await fetchFees();
      setSuccessMessage('Fee saved as draft successfully!');
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error saving fee:', error);
      handleFeeSaveError(error, 'Failed to save fee. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const feeData = {
        ...formData,
        fee_category: formData.fee_category.trim(),
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        status: 'published',
        late_fee_amount: formData.late_fee_amount ? parseFloat(formData.late_fee_amount) : null,
        updated_at: new Date().toISOString()
      };

      if (editingFee) {
        // Update existing fee
        const { error } = await supabase
          .from('annual_fees_master')
          .update(feeData)
          .eq('id', editingFee.id);

        if (error) throw error;
      } else {
        // Create new fee
        const { error } = await supabase
          .from('annual_fees_master')
          .insert(feeData);

        if (error) throw error;
      }

      // Reset form
      setFormData({
        fee_code: '',
        fee_category: '',
        billing_frequency: '',
        is_mandatory: false,
        discount_applicable: false,
        late_fee_amount: '',
        status: 'draft'
      });
      setEditingFee(null);
      setErrors({});
      await fetchFees();
      setSuccessMessage('Fee published successfully! 🎉');
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error publishing fee:', error);
      handleFeeSaveError(error, 'Failed to publish fee. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    setFormData({
      fee_code: fee.fee_code || '',
      fee_category: fee.fee_category || '',
      billing_frequency: fee.billing_frequency || '',
      is_mandatory: fee.is_mandatory || false,
      discount_applicable: fee.discount_applicable || false,
      late_fee_amount: fee.late_fee_amount || '',
      status: fee.status || 'draft'
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (feeId) => {
    if (!window.confirm('Are you sure you want to delete this fee?')) return;

    try {
      const { error } = await supabase
        .from('annual_fees_master')
        .delete()
        .eq('id', feeId);

      if (error) throw error;

      await fetchFees();
      alert('Fee deleted successfully!');
    } catch (error) {
      console.error('Error deleting fee:', error);
      alert('Failed to delete fee. Please try again.');
    }
  };

  const handleCancel = () => {
    setFormData({
      fee_code: '',
      fee_category: '',
      billing_frequency: '',
      is_mandatory: false,
      discount_applicable: false,
      late_fee_amount: '',
      status: 'draft'
    });
    setEditingFee(null);
    setErrors({});
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>Annual Fees Master</h1>
          <p>Define and manage fee codes for your school</p>
        </div>

        <div className="branch-filter-row" style={{ marginBottom: '1.5rem' }}>
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
        </div>

        <div className="dashboard-section">
          <h2>{editingFee ? 'Edit Fee' : 'Add New Fee'}</h2>
          
          <div className="form-section" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="form-group">
              <label htmlFor="fee_code">
                Fee Code <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="text"
                id="fee_code"
                name="fee_code"
                value={formData.fee_code}
                onChange={handleInputChange}
                placeholder="e.g., TUI-001, DEV-001"
                className={errors.fee_code ? 'error' : ''}
              />
              {errors.fee_code && (
                <span className="error-text">{errors.fee_code}</span>
              )}
              <small style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                Unique code used as system reference
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="fee_category">
                Fee Category <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="text"
                id="fee_category"
                name="fee_category"
                value={formData.fee_category}
                onChange={handleInputChange}
                placeholder="e.g., Tuition, Development, Transport, Lab Fee"
                className={errors.fee_category ? 'error' : ''}
              />
              {errors.fee_category && (
                <span className="error-text">{errors.fee_category}</span>
              )}
              <small style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                Type any fee category name for this school
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="billing_frequency">
                Billing Frequency <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <select
                id="billing_frequency"
                name="billing_frequency"
                value={formData.billing_frequency}
                onChange={handleInputChange}
                className={errors.billing_frequency ? 'error' : ''}
              >
                <option value="">Select Billing Frequency</option>
                {billingFrequencies.map(frequency => (
                  <option key={frequency} value={frequency}>{frequency}</option>
                ))}
              </select>
              {errors.billing_frequency && (
                <span className="error-text">{errors.billing_frequency}</span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_mandatory"
                    checked={formData.is_mandatory}
                    onChange={handleInputChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>Is Mandatory</span>
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="discount_applicable"
                    checked={formData.discount_applicable}
                    onChange={handleInputChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>Discount Applicable</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="late_fee_amount">Late Fee Amount</label>
              <input
                type="number"
                id="late_fee_amount"
                name="late_fee_amount"
                value={formData.late_fee_amount}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={errors.late_fee_amount ? 'error' : ''}
              />
              {errors.late_fee_amount && (
                <span className="error-text">{errors.late_fee_amount}</span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSubmitting}
                className="btn-secondary"
                style={{
                  flex: 1,
                  backgroundColor: '#FFFFFF',
                  color: '#374151',
                  border: '1px solid #D1D5DB'
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={isSubmitting}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                {isSubmitting ? 'Publishing...' : 'Publish Fees'}
              </button>
              {editingFee && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                  style={{
                    backgroundColor: '#FFFFFF',
                    color: '#374151',
                    border: '1px solid #D1D5DB'
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Fee Codes List</h2>
          
          {loadingFees ? (
            <p>Loading fees...</p>
          ) : fees.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              No fees defined yet. Add your first fee code above.
            </p>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fee Code</th>
                    <th>Category</th>
                    <th>Billing Frequency</th>
                    <th>Mandatory</th>
                    <th>Discount</th>
                    <th>Late Fee</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fees.map((fee) => (
                    <tr key={fee.id}>
                      <td>{fee.fee_code}</td>
                      <td>{fee.fee_category}</td>
                      <td>{fee.billing_frequency}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: fee.is_mandatory ? '#10B981' : '#E5E7EB',
                          color: fee.is_mandatory ? 'white' : '#374151',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}>
                          {fee.is_mandatory ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: fee.discount_applicable ? '#3B82F6' : '#E5E7EB',
                          color: fee.discount_applicable ? 'white' : '#374151',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}>
                          {fee.discount_applicable ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{fee.late_fee_amount ? `₨${parseFloat(fee.late_fee_amount).toFixed(2)}` : 'N/A'}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: fee.status === 'published' ? '#10B981' : '#F59E0B',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}>
                          {fee.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleEdit(fee)}
                            style={{ 
                              padding: '0.5rem 1rem', 
                              fontSize: '0.875rem',
                              backgroundColor: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#10B981'}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(fee.id)}
                            style={{ 
                              padding: '0.5rem 1rem', 
                              fontSize: '0.875rem',
                              backgroundColor: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#DC2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#EF4444'}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowSuccessPopup(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#10B981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              animation: 'scaleIn 0.3s ease-out'
            }}>
              <span style={{ fontSize: '2.5rem', color: 'white' }}>✓</span>
            </div>
            <h2 style={{
              margin: '0 0 1rem 0',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700'
            }}>
              Success!
            </h2>
            <p style={{
              margin: '0 0 2rem 0',
              color: '#6B7280',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {successMessage}
            </p>
            <button
              onClick={() => setShowSuccessPopup(false)}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#FF8C42',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 6px rgba(255, 139, 61, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#E67E22';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 12px rgba(255, 139, 61, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#FF8C42';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px rgba(255, 139, 61, 0.3)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default AnnualFeesMaster;
